import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Chip, Stack, Theme, Typography, useTheme } from '@mui/material';

import { useCreatorPostVisuals } from '@/contexts/CreatorDetailsContext';
import { Posts } from '@/models/posts';
import { msToPrettyTimestamp, msToSeconds } from '@/utils/datesAndTime';

interface MinorSquareProps {
  obj: Posts.MinorItem | Posts.VideoMinorItem;
  index: number;
  isSelected: boolean;
  theme: Theme;
  onClick: () => void;
}

const MinorSquare = ({
  obj,
  index,
  isSelected,
  theme,
  onClick
}: MinorSquareProps) => {
  const isVideoMinorItem = 'timestamp' in obj;
  const minorsFound = isVideoMinorItem
    ? (obj as Posts.VideoMinorItem).minorsFound
    : [];

  const renderBox = (key: number) => (
    <Box
      key={key}
      display="flex"
      width="78px"
      height="78px"
      paddingY="27px"
      alignItems="center"
      justifyContent="center"
      color={theme.palette.secondary.contrastText}
      sx={{
        backgroundColor: theme.palette.primary.main,
        transition: 'all 0.3s ease',
        position: 'relative',
        zIndex: isSelected ? 1 : 0
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        {index}
      </Typography>
    </Box>
  );

  const renderTimestamp = () => (
    <Typography variant="caption" fontSize={10} fontWeight={600}>
      {msToPrettyTimestamp((obj as Posts.VideoMinorItem).timestamp)}
    </Typography>
  );

  const sharedSx = {
    backgroundColor: isSelected ? 'rgba(162, 130, 83, 0.30)' : 'transparent',
    transition: 'all 0.3s ease',
    position: 'relative' as const,
    zIndex: isSelected ? 1 : 0,
    cursor: 'pointer'
  };

  if (minorsFound.length > 1) {
    return (
      <Stack
        direction="row"
        gap={1.8}
        border={`1px solid ${
          isSelected ? theme.palette.secondary.main : theme.palette.primary.main
        }`}
        onClick={onClick}
        sx={{
          ...sharedSx,
          backgroundColor: isSelected
            ? 'rgba(162, 130, 83, 0.30)'
            : 'rgba(0, 0, 0, 0.30)'
        }}
      >
        {minorsFound.map((_, i) => (
          <Stack padding="5px" key={i}>
            {renderBox(i)}
            {renderTimestamp()}
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      padding="5px"
      onClick={onClick}
      sx={{
        ...sharedSx,
        border: isSelected
          ? `1px solid ${theme.palette.secondary.main}`
          : 'none'
      }}
    >
      {renderBox(0)}
      {isVideoMinorItem && renderTimestamp()}
    </Stack>
  );
};

interface CreatorPostChildrenProps {
  media: Posts.Media[];
  setVideoPlaybackStart?: (seconds: number) => void;
  setVideoLastClick?: (ts: number) => void;
}

const CreatorPostChildren: FC<CreatorPostChildrenProps> = ({
  media,
  setVideoPlaybackStart,
  setVideoLastClick
}) => {
  const theme = useTheme();
  const { selectedMinorItems, setSelectedMinorItems, clearSelections } =
    useCreatorPostVisuals();

  const [isHighlighted0to6, setHighlighted0to6] = useState(false);
  const [isHighlighted7to14, setHighlighted7to14] = useState(false);
  const [isHighlighted15to17, setHighlighted15to17] = useState(false);

  const [ageRange, setAgeRange] = useState({ min: 0, max: 17 });

  const minors = useMemo(
    () =>
      media.map(
        (mediaItem) =>
          mediaItem.visualRecognition
            ?.find((item) => item.type === 'minors')
            ?.items.filter(
              (item): item is Posts.MinorItem | Posts.VideoMinorItem =>
                'minorsFound' in item || 'coordinates' in item
            ) ?? []
      ),
    [media]
  );

  const activeMinors = minors[0] ?? [];
  const hasMinorsResults = activeMinors.length > 0;

  const getMinorsInRange = useCallback(
    (min: number, max: number) =>
      activeMinors.flatMap((obj) => {
        const isVideo = 'timestamp' in obj;
        const filtered = isVideo
          ? (obj as Posts.VideoMinorItem).minorsFound.filter(
              (m) => m.predictedAge >= min && m.predictedAge <= max
            )
          : [obj as Posts.MinorItem].filter(
              (m) => m.predictedAge >= min && m.predictedAge <= max
            );
        return filtered.length > 0 ? [{ ...obj, minorsFound: filtered }] : [];
      }),
    [activeMinors]
  );

  useEffect(() => {
    let newMin = 0;
    let newMax = 17;

    const ranges = [
      isHighlighted0to6 && { min: 0, max: 6 },
      isHighlighted7to14 && { min: 7, max: 14 },
      isHighlighted15to17 && { min: 15, max: 17 }
    ].filter((range): range is { min: number; max: number } => Boolean(range));

    if (ranges.length > 0) {
      newMin = Math.min(...ranges.map((r) => r.min));
      newMax = Math.max(...ranges.map((r) => r.max));
    }

    clearSelections();
    setAgeRange({ min: newMin, max: newMax });
  }, [isHighlighted0to6, isHighlighted7to14, isHighlighted15to17]);

  const minorResultsChips = useMemo(
    () => [
      {
        type: '0-6',
        label: 'Age 0-6',
        onClick: () => setHighlighted0to6((prev) => !prev),
        isVisible: getMinorsInRange(0, 6).length > 0,
        active: isHighlighted0to6
      },
      {
        type: '7-14',
        label: 'Age 7-14',
        onClick: () => setHighlighted7to14((prev) => !prev),
        isVisible: getMinorsInRange(7, 14).length > 0,
        active: isHighlighted7to14
      },
      {
        type: '15-17',
        label: 'Age 15-17',
        onClick: () => setHighlighted15to17((prev) => !prev),
        isVisible: getMinorsInRange(15, 17).length > 0,
        active: isHighlighted15to17
      }
    ],
    [
      isHighlighted0to6,
      isHighlighted7to14,
      isHighlighted15to17,
      getMinorsInRange
    ]
  );

  const handleMinorClick = useCallback(
    (obj: Posts.MinorItem | Posts.VideoMinorItem) => {
      if ('timestamp' in obj) {
        const videoObj = obj as Posts.VideoMinorItem;
        const isAlreadySelected = selectedMinorItems.some(
          (s) => JSON.stringify(s) === JSON.stringify(videoObj.minorsFound[0])
        );
        setSelectedMinorItems(
          isAlreadySelected
            ? selectedMinorItems.filter(
                (s) =>
                  !videoObj.minorsFound.some(
                    (m) => JSON.stringify(s) === JSON.stringify(m)
                  )
              )
            : [...videoObj.minorsFound]
        );
        if (setVideoPlaybackStart && setVideoLastClick) {
          setVideoPlaybackStart(msToSeconds(videoObj.timestamp));
          setVideoLastClick(Date.now());
        }
      } else {
        const imageObj = obj as Posts.MinorItem;
        const isAlreadySelected = selectedMinorItems.some(
          (s) => JSON.stringify(s) === JSON.stringify(imageObj)
        );
        setSelectedMinorItems(
          isAlreadySelected
            ? selectedMinorItems.filter(
                (s) => JSON.stringify(s) !== JSON.stringify(imageObj)
              )
            : [...selectedMinorItems, imageObj]
        );
      }
    },
    [
      selectedMinorItems,
      setSelectedMinorItems,
      setVideoPlaybackStart,
      setVideoLastClick
    ]
  );

  if (!hasMinorsResults) {
    return <Typography variant="body2">No children detected</Typography>;
  }

  return (
    <Stack gap={2}>
      {/* Age filter chips */}
      <Stack
        border={`1px solid ${theme.palette.grey[400]}`}
        borderRadius="4px"
        gap={1}
        padding={1}
        flexWrap="wrap"
        width="fit-content"
      >
        <Stack direction="row" gap={1} flexWrap="wrap">
          {minorResultsChips
            .filter((chip) => chip.isVisible)
            .map((chip) => (
              <Chip
                key={chip.type}
                onClick={chip.onClick}
                color="primary"
                label={chip.label}
                size="small"
                variant={chip.active ? 'filled' : 'outlined'}
              />
            ))}
        </Stack>
      </Stack>
      {/* Minor squares */}
      <Stack
        gap={2}
        maxHeight="265px"
        direction="row"
        alignSelf="stretch"
        flexWrap="wrap"
        sx={{ overflowY: 'auto' }}
      >
        {getMinorsInRange(ageRange.min, ageRange.max).map((obj, index) => {
          const isVideo = 'timestamp' in obj;
          const isSelected = isVideo
            ? (obj as Posts.VideoMinorItem).minorsFound.every((minor) =>
                selectedMinorItems.some(
                  (s) => JSON.stringify(s) === JSON.stringify(minor)
                )
              )
            : selectedMinorItems.some(
                (s) => JSON.stringify(s) === JSON.stringify(obj)
              );

          return (
            <MinorSquare
              key={index}
              obj={obj}
              index={index + 1}
              isSelected={isSelected}
              theme={theme}
              onClick={() => handleMinorClick(obj)}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default CreatorPostChildren;
